
import {
  defineComponent,
  defineExpose,
  reactive,
  ref,
  computed,
  onMounted,
} from 'vue';

import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Dropdown from 'primevue/dropdown';
import ConfirmPopup from 'primevue/confirmpopup';

import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import Loader from '@/components/layout/Loader.vue';
import BaseInputNumber from '@/components/fields/partials/BaseInputNumber.vue';

import { showToastError, showToastSuccess } from '@/helpers';
import {
  fetchShopConfigurationsNest,
  saveShopConfigurationsNest,
  saveShopParamsOnRedis,
} from '@/composables/shop/ShopsConfiguration';
import { getSalesAssociationChannels, SendingChannel } from '@/configs/sending-channels';

import { useConfirm } from 'primevue/useconfirm';
import { useI18n } from 'vue-i18n';

type DelayUnit = 'hours' | 'days';
type DelayAction = 'open_click' | 'click';
type GlobalType = 'automation' | 'bulk';

interface SalesAssociationDelay {
  delay: {
    value: number;
    unit: DelayUnit;
  };
  action: DelayAction;
}

const GLOBAL_TYPES: GlobalType[] = ['automation', 'bulk'];

const MAX_HOURS = 5 * 24;
const MAX_DAYS = 5;
const MIN_HOURS = 1;
const MIN_DAYS = 1;

const buildDefaults = (
  channels: SendingChannel[],
): Record<GlobalType, Record<string, SalesAssociationDelay>> => {
  const create = (): Record<string, SalesAssociationDelay> => channels.reduce((acc, channel) => {
    acc[channel.key] = {
      delay: { value: 5, unit: 'days' },
      action: channel.supportsOpenClick ? 'open_click' : 'click',
    };
    return acc;
  }, {} as Record<string, SalesAssociationDelay>);

  return {
    automation: create(),
    bulk: create(),
  };
};

const buildKey = (globalType: GlobalType, mediaKey: string) => `shopRequiredDataOrdersAssociationConfiguration_${globalType}_${mediaKey}`;

export default defineComponent({
  name: 'SalesAssociationServiceParameter',

  components: {
    TabView,
    TabPanel,
    Dropdown,
    ConfirmPopup,
    SpmButton,
    Loader,
    BaseInputNumber,
  },

  props: {
    shopId: {
      type: Number,
      required: true,
    },

    globalSave: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['close'],

  setup(props) {
    const { t } = useI18n();
    const confirm = useConfirm();

    const isLoading = ref(false);
    const saveLoading = ref(false);
    const resetLoading = ref(false);
    const activeTab = ref(0);

    const channels: SendingChannel[] = getSalesAssociationChannels();

    const configurations = reactive(buildDefaults(channels));

    const allConfigKeys = (): string[] => {
      const keys: string[] = [];
      GLOBAL_TYPES.forEach((globalType) => {
        channels.forEach((channel) => {
          keys.push(buildKey(globalType, channel.key));
        });
      });
      return keys;
    };

    const applyDefaults = () => {
      const defaults = buildDefaults(channels);
      GLOBAL_TYPES.forEach((globalType) => {
        channels.forEach((channel) => {
          const target = configurations[globalType][channel.key];
          const def = defaults[globalType][channel.key];
          target.delay.value = def.delay.value;
          target.delay.unit = def.delay.unit;
          target.action = def.action;
        });
      });
    };

    const confirmReset = (event: Event) => {
      confirm.require({
        group: 'basicGroup',
        target: (event as MouseEvent).currentTarget as HTMLElement,
        message: t('storeParameters.services.salesAssociationServiceParameter.resetConfirmMessage'),
        header: t('storeParameters.services.salesAssociationServiceParameter.resetConfirmHeader'),
        icon: 'far fa-exclamation-triangle',
        acceptLabel: t('yes'),
        rejectLabel: t('no'),
        acceptClass: 'p-button-danger',
        rejectClass: 'p-button-secondary',
        accept: async () => {
          resetLoading.value = true;
          try {
            applyDefaults();
            const result = await saveShopConfigurationsNest(props.shopId, buildPayload());
            if (!result) {
              showToastError('GENERIC_ERROR');
              return;
            }
            await saveShopParamsOnRedis(props.shopId);
            showToastSuccess('savedSuccessful');
          } catch (err) {
            showToastError('GENERIC_ERROR');
          } finally {
            resetLoading.value = false;
          }
        },
      });
    };

    const unitOptions = computed(() => [
      { label: t('storeParameters.services.salesAssociationServiceParameter.units.hours'), value: 'hours' },
      { label: t('storeParameters.services.salesAssociationServiceParameter.units.days'), value: 'days' },
    ]);

    const actionOptions = computed(() => [
      { label: t('storeParameters.services.salesAssociationServiceParameter.actions.open_click'), value: 'open_click' },
      { label: t('storeParameters.services.salesAssociationServiceParameter.actions.click'), value: 'click' },
    ]);

    const getMin = (unit: DelayUnit) => (unit === 'hours' ? MIN_HOURS : MIN_DAYS);
    const getMax = (unit: DelayUnit) => (unit === 'hours' ? MAX_HOURS : MAX_DAYS);

    const clamp = (value: number, unit: DelayUnit) => {
      const min = getMin(unit);
      const max = getMax(unit);
      if (Number.isNaN(value) || value < min) return min;
      if (value > max) return max;
      return Math.floor(value);
    };

    const onDelayInputHandler = (globalType: GlobalType, mediaKey: string) => (value: number) => {
      const target = configurations[globalType][mediaKey];
      target.delay.value = clamp(value, target.delay.unit);
    };

    const onUnitChange = (globalType: GlobalType, mediaKey: string) => {
      const target = configurations[globalType][mediaKey];
      target.delay.value = clamp(target.delay.value, target.delay.unit);
    };

    const buildPayload = () => {
      const out: Array<{ key: string; value: string; lang: string | null }> = [];
      GLOBAL_TYPES.forEach((globalType) => {
        channels.forEach((channel) => {
          const cfg = configurations[globalType][channel.key];
          out.push({
            key: buildKey(globalType, channel.key),
            value: JSON.stringify({
              delay: {
                value: clamp(cfg.delay.value, cfg.delay.unit),
                unit: cfg.delay.unit,
              },
              action: cfg.action,
            }),
            lang: null,
          });
        });
      });
      return out;
    };

    const handleSave = async () => {
      const payload = buildPayload();

      if (props.globalSave) {
        return {
          err: null,
          configs: payload.map((c) => ({ key: c.key, value: c.value, lang: c.lang || '' })),
        };
      }

      saveLoading.value = true;
      try {
        const result = await saveShopConfigurationsNest(props.shopId, payload);
        if (result) {
          await saveShopParamsOnRedis(props.shopId);
          showToastSuccess('savedSuccessful');
        } else {
          showToastError('GENERIC_ERROR');
        }
      } catch (err) {
        showToastError('GENERIC_ERROR');
      } finally {
        saveLoading.value = false;
      }
      return undefined;
    };

    defineExpose({ handleSave });

    onMounted(async () => {
      try {
        isLoading.value = true;
        const data = await fetchShopConfigurationsNest(props.shopId, allConfigKeys());
        if (!data) return;

        GLOBAL_TYPES.forEach((globalType) => {
          channels.forEach((channel) => {
            const stored = data[buildKey(globalType, channel.key)];
            if (!stored) return;

            const parsed = typeof stored === 'string' ? safeJsonParse(stored) : stored;
            if (!parsed || typeof parsed !== 'object') return;

            const target = configurations[globalType][channel.key];
            const unit: DelayUnit = parsed.delay?.unit === 'hours' ? 'hours' : 'days';
            const value = Number(parsed.delay?.value);
            target.delay.unit = unit;
            target.delay.value = clamp(Number.isFinite(value) ? value : 5, unit);
            if (!channel.supportsOpenClick) {
              target.action = 'click';
            } else {
              target.action = parsed.action === 'click' ? 'click' : 'open_click';
            }
          });
        });
      } catch (err) {
        showToastError('GENERIC_ERROR');
      } finally {
        isLoading.value = false;
      }
    });

    function safeJsonParse(value: string) {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }

    return {
      t,
      isLoading,
      saveLoading,
      resetLoading,
      activeTab,
      configurations,
      globalTypes: GLOBAL_TYPES,
      mediaList: channels,
      unitOptions,
      actionOptions,
      getMin,
      getMax,
      onDelayInputHandler,
      onUnitChange,
      handleSave,
      confirmReset,
    };
  },
});
